/**
 * @module lib/imageUpload
 * @description Converts an uploaded photo into a web-ready JPEG data URL,
 * center-cropped to the frame it will fill and capped in width to keep
 * storage small.
 */

/** Story card photo frame (1254 × 919, measured from the print file). */
export const PHOTO_ASPECT = 1254 / 919;
/** Thank-you front portrait frame (577 × 728, measured from Samantha's PNG). */
export const PORTRAIT_ASPECT = 577 / 728;
/** Enough for 300 dpi print across the ~5.3 in golden-major photo region. */
const MAX_WIDTH_PX = 1600;

/** Loads a File into an HTMLImageElement. */
function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => reject(new Error(`Could not read image "${file.name}".`));
        img.src = url;
    });
}

/**
 * Center-crops to `aspect` (or keeps the whole image when `aspect` is null,
 * e.g. a scanned note) and re-encodes as JPEG.
 * @returns data URL plus pixel size, so admin can warn on low-resolution sources.
 */
export async function prepareCardImage(
    file: File,
    aspect: number | null = PHOTO_ASPECT
): Promise<{ dataUrl: string; sourceWidth: number; sourceHeight: number }> {
    if (!file.type.startsWith("image/")) throw new Error("Please choose an image file (JPG, PNG, WebP or HEIC).");
    const img = await loadImage(file);
    const srcAspect = img.naturalWidth / img.naturalHeight;
    const target = aspect ?? srcAspect;
    const cropW = srcAspect > target ? img.naturalHeight * target : img.naturalWidth;
    const cropH = srcAspect > target ? img.naturalHeight : img.naturalWidth / target;
    const outW = Math.min(MAX_WIDTH_PX, Math.round(cropW));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = Math.round(outW / target);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas is unavailable in this browser.");
    ctx.drawImage(img, (img.naturalWidth - cropW) / 2, (img.naturalHeight - cropH) / 2, cropW, cropH, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/jpeg", 0.85), sourceWidth: img.naturalWidth, sourceHeight: img.naturalHeight };
}

/** Below this source width the photo prints under ~280 dpi; admin shows a warning. */
export const PRINT_MIN_WIDTH_PX = 1500;
